import { useCallback, useEffect, useState } from 'react';
import type { UseMultiplayerReturn } from './useMultiplayer';
import type {
  VoiceSessionInfo,
  VoiceConnectionStatus,
  SpeakerMapping,
} from '@roll1/shared';

/**
 * Live voice session state on the frontend.
 * Subscribes to `voice:status-updated` WS events emitted by the server when
 * the Discord bot connects/disconnects, transcript is received, STT warning
 * threshold is crossed, or GM updates speaker mappings.
 *
 * Consumers:
 * - <VoiceSessionIndicator> — displays status + STT duration
 * - <SpeakerMappingModal> — lets GM manually map Discord users → roll1 participants
 * - Session toast/banner for STT warnings
 */
export interface UseVoiceSessionReturn {
  /** Current connection status — 'disconnected' when bot is not in any voice channel */
  status: VoiceConnectionStatus;
  /** Full voice session info (guild, channel, mappings, duration) — null if never connected */
  info: VoiceSessionInfo | null;
  /** Most recent warning/info message from server (3.5h STT warning, limit reached, etc.) */
  warningMessage: string | null;
  /** Request fresh status from server (called once on mount, idempotent) */
  refreshStatus: () => void;
  /** GM-only: update speaker mappings (Discord user → roll1 participant) */
  updateMappings: (mappings: SpeakerMapping[]) => void;
  /** Clear the warning banner (e.g., after user dismisses it) */
  dismissWarning: () => void;
}

export function useVoiceSession(mp: UseMultiplayerReturn): UseVoiceSessionReturn {
  const [status, setStatus] = useState<VoiceConnectionStatus>('disconnected');
  const [info, setInfo] = useState<VoiceSessionInfo | null>(null);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);

  // Subscribe to voice:status-updated broadcasts
  useEffect(() => {
    const unsub = mp.onMessage((msg) => {
      if (msg.type !== 'voice:status-updated') return;

      const update = msg as {
        type: 'voice:status-updated';
        status: VoiceConnectionStatus;
        info?: Partial<VoiceSessionInfo>;
      };

      setStatus(update.status);

      if (update.info) {
        setInfo(prev => {
          if (prev) {
            // Partial update: merge into existing info
            return { ...prev, ...update.info };
          }
          // Initial snapshot from server — construct minimal VoiceSessionInfo
          return {
            status: update.status,
            guildId: update.info?.guildId ?? '',
            channelId: update.info?.channelId ?? '',
            speakerMappings: update.info?.speakerMappings ?? [],
            connectedAt: update.info?.connectedAt,
            totalSttDurationSec: update.info?.totalSttDurationSec ?? 0,
            language: update.info?.language ?? 'ru',
            warningBroadcast: update.info?.warningBroadcast,
            warningMessage: update.info?.warningMessage,
          };
        });

        if (update.info.warningMessage) {
          setWarningMessage(update.info.warningMessage);
        }
      }

      // Clear warning when disconnecting
      if (update.status === 'disconnected') {
        setWarningMessage(null);
      }
    });

    return unsub;
  }, [mp]);

  // Request status snapshot on mount (and re-request on reconnect)
  const refreshStatus = useCallback(() => {
    if (mp.status !== 'connected') return;
    mp.send({ type: 'voice:status-request' });
  }, [mp]);

  useEffect(() => {
    // Auto-refresh when multiplayer connects (handles initial load + reconnects)
    if (mp.status === 'connected') {
      refreshStatus();
    }
  }, [mp.status, refreshStatus]);

  const updateMappings = useCallback(
    (mappings: SpeakerMapping[]) => {
      mp.send({ type: 'voice:mapping-update', mappings });
    },
    [mp],
  );

  const dismissWarning = useCallback(() => {
    setWarningMessage(null);
  }, []);

  return {
    status,
    info,
    warningMessage,
    refreshStatus,
    updateMappings,
    dismissWarning,
  };
}
